/**
 * The one fetch the passkey and step-up calls share.
 *
 * Spelled out rather than routed through `membersApi`, for the same reason
 * `cliDeviceApproval.ts` is: the pages need the HTTP status and the server's
 * error code, not just the body. A 403 `reauth_required` has to be told apart
 * from any other refusal, and a 404 challenge from a failed signature. The
 * IP-block check still runs, through `readBody`.
 *
 * Request bodies are snake_case, which is what the members controllers
 * declare. Response data is camelCase, as everywhere else.
 */
import { API_BASE, getSubdomain, refresh } from "modules/members/api";
import { readBody } from "modules/members/api/ipRestriction";
import { useMemberAuthStore } from "modules/members/auth";
import {
  NETWORK_MESSAGE,
  SESSION_MESSAGE,
  toFailure,
  type StepUpFailure,
} from "./stepUpFailure";

type Method = "GET" | "POST" | "PATCH" | "DELETE";

const currentToken = async (): Promise<string> => {
  const current = useMemberAuthStore.getState().accessToken;
  if (current) return current;
  const refreshed = await refresh().catch(() => null);
  if (refreshed?.accessToken) return refreshed.accessToken;
  throw { status: 401, code: null, message: SESSION_MESSAGE } as StepUpFailure;
};

const send = async (
  method: Method,
  path: string,
  body: unknown,
  token: string,
): Promise<{ status: number; ok: boolean; parsed: unknown }> => {
  let res: Response;
  try {
    res = await fetch(API_BASE + path, {
      method,
      headers: {
        "content-type": "application/json",
        authorization: "Bearer " + token,
        "x-org-subdomain": getSubdomain(),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw { status: null, code: null, message: NETWORK_MESSAGE } as StepUpFailure;
  }
  // An error page from a proxy is not JSON; that is a failure to describe,
  // not a crash.
  const parsed = await readBody<unknown>(res).catch(() => null);
  return { status: res.status, ok: res.ok, parsed };
};

/**
 * Calls a members endpoint and returns its `data`, or throws a StepUpFailure.
 * An expired access token is refreshed once and the call repeated, the same
 * courtesy `membersApi.get` extends.
 */
export const memberCall = async <T>(
  method: Method,
  path: string,
  body: unknown,
  fallback: string,
): Promise<T> => {
  let result = await send(method, path, body, await currentToken());

  if (result.status === 401) {
    const refreshed = await refresh().catch(() => null);
    if (refreshed?.accessToken) {
      result = await send(method, path, body, refreshed.accessToken);
    }
  }

  const envelope =
    result.parsed && typeof result.parsed === "object"
      ? (result.parsed as { success?: unknown; data?: unknown })
      : null;
  if (result.ok && envelope?.success === true) return envelope.data as T;
  throw toFailure(result.status, result.parsed, fallback);
};
